import { useRef, useEffect, useState } from 'react';
import Header from '@/components/feature/Header';
import BasicInfoPanel from './components/BasicInfoPanel';
import CourseMaterialsPanel from './components/CourseMaterialsPanel';
import AIRadarPanel from './components/AIRadarPanel';
import DimensionLineChart from './components/DimensionLineChart';
import AICommentPanel from './components/AICommentPanel';
import ImprovementPanel from './components/ImprovementPanel';
import EmptyMaterialsPanel from './components/EmptyMaterialsPanel';
import EmptyEvalCenter from './components/EmptyEvalCenter';
import EmptyRightPanel from './components/EmptyRightPanel';

// ── 粒子动画 ──
interface Particle { x: number; y: number; vx: number; vy: number; r: number; opacity: number; color: string; }

function ParticleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);
    const COLORS = ['#22d3ee', '#14b8a6', '#10b981', '#a78bfa', '#fbbf24'];
    const COUNT = 45;
    const pts: Particle[] = Array.from({ length: COUNT }, () => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
      r: Math.random() * 1.6 + 0.4,
      opacity: Math.random() * 0.45 + 0.12,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x; const dy = pts[i].y - pts[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 110) {
            ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = `rgba(34,211,238,${0.05 * (1 - dist / 110)})`; ctx.lineWidth = 0.5; ctx.stroke();
          }
        }
      }
      pts.forEach(p => {
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.globalAlpha = p.opacity; ctx.fillStyle = p.color;
        ctx.shadowBlur = 6; ctx.shadowColor = p.color; ctx.fill();
        ctx.globalAlpha = 1; ctx.shadowBlur = 0;
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = canvas.width; if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height; if (p.y > canvas.height) p.y = 0;
      });
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(rafRef.current); window.removeEventListener('resize', resize); };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" style={{ zIndex: 1, opacity: 0.65 }} />;
}

import { useParams } from 'react-router-dom';
import { EvalProvider, useEval, useEvalDispatch } from '@/context/EvalContext';

// ── 面板容器 ──
interface PanelCardProps {
  children: React.ReactNode;
  accentColor?: string;
  className?: string;
  style?: React.CSSProperties;
}

function PanelCard({ children, accentColor = '#22d3ee', className = '', style }: PanelCardProps) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={`rounded-xl overflow-hidden relative ${className}`}
      style={{
        background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(10,20,40,0.82) 60%, rgba(5,12,25,0.9) 100%)',
        border: `1px solid ${hovered ? accentColor + '50' : accentColor + '25'}`,
        boxShadow: hovered
          ? `0 0 28px ${accentColor}20, inset 0 1px 0 rgba(255,255,255,0.06)`
          : `0 0 14px ${accentColor}0a, inset 0 1px 0 rgba(255,255,255,0.04)`,
        backdropFilter: 'blur(16px)',
        transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
        ...style,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* 顶部高光 */}
      <div className="absolute top-0 left-0 right-0 h-px pointer-events-none"
        style={{ background: `linear-gradient(to right, transparent 5%, ${accentColor}65 40%, ${accentColor}88 50%, ${accentColor}65 60%, transparent 95%)`, zIndex: 2 }} />
      {/* 左侧竖线 */}
      <div className="absolute top-0 left-0 bottom-0 w-px pointer-events-none"
        style={{ background: `linear-gradient(to bottom, ${accentColor}55, transparent 60%)`, zIndex: 2 }} />
      {children}
    </div>
  );
}

type PageState = 'empty' | 'evaluating' | 'done';

function TeacherDetailPageInner() {
  const [pageState, setPageState] = useState<PageState>('empty');
  const [evalProgress, setEvalProgress] = useState(0);
  const [uploadedVideoFile, setUploadedVideoFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { id } = useParams();

  const setEvalData = useEvalDispatch();

  const handleReset = () => {
    setPageState('empty');
    setUploadedVideoFile(null);
    if (setEvalData) setEvalData(null);
  };

  const handleStartEval = async () => {
    setPageState('evaluating');
    setEvalProgress(0);

    let targetId = id;
    if (uploadedVideoFile) {
      try {
        const rawName = uploadedVideoFile.name.replace(/\.[^/.]+$/, ""); // 去掉扩展名
        const res = await fetch('/data/video_index.json');
        const list = await res.json();
        const found = list.find((item: any) => item.videoFilename.includes(rawName) || rawName.includes(item.videoFilename) || rawName.includes(item.teacherName));
        if (found) targetId = found.id;
      } catch (e) { }
    }

    const baseUrl = import.meta.env.BASE_URL || '/';
    const filePath = `${baseUrl.replace(/\/$/, '')}/data/${targetId}.json`;

    // Async fetch mock data based on targetId, adding timestamp to bust cache in dev environment
    fetch(`${filePath}?t=${Date.now()}`).then(async res => { if (!res.ok) throw new Error("JSON Not Found"); const text = await res.text(); if (text.trim().startsWith("<")) throw new Error("Received HTML instead of JSON"); return JSON.parse(text); }).then(data => { if (setEvalData) setEvalData(data); }).catch(err => { console.error("加载数据失败", err); alert(`【演示数据未命中】\n\n当前视频对应的数据预构件缺失。\n目标读取路径: ${filePath}\n\n如有新视频加载请确保跑批脚本生成了相应的映射 JSON。`); });

    const start = Date.now();
    const DURATION = 3200;
    const tick = () => {
      const t = Math.min((Date.now() - start) / DURATION, 1);
      setEvalProgress(Math.round(t * 100));
      if (t < 1) requestAnimationFrame(tick);
      else setPageState('done');
    };
    requestAnimationFrame(tick);
  };

  return (
    <div
      className="relative overflow-hidden"
      style={{
        width: '100vw', height: '100vh',
        display: 'flex', flexDirection: 'column',
        background: 'linear-gradient(135deg, #021a3a 0%, #0c2b54 35%, #072248 65%, #021a3a 100%)',
      }}
    >
      {/* 背景网格 */}
      <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.1, zIndex: 0 }}>
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="52" height="52" patternUnits="userSpaceOnUse">
              <path d="M 52 0 L 0 0 0 52" fill="none" stroke="#22d3ee" strokeWidth="0.3" />
            </pattern>
            <pattern id="grid-lg" width="208" height="208" patternUnits="userSpaceOnUse">
              <path d="M 208 0 L 0 0 0 208" fill="none" stroke="#22d3ee" strokeWidth="0.7" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
          <rect width="100%" height="100%" fill="url(#grid-lg)" opacity="0.5" />
        </svg>
      </div>

      {/* 粒子层 */}
      <ParticleCanvas />

      {/* 光晕 */}
      <div className="absolute pointer-events-none" style={{ zIndex: 1, top: '6%', left: '10%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(34,211,238,0.05) 0%, transparent 65%)', borderRadius: '50%' }} />
      <div className="absolute pointer-events-none" style={{ zIndex: 1, bottom: '6%', right: '10%', width: '420px', height: '420px', background: 'radial-gradient(circle, rgba(16,185,129,0.04) 0%, transparent 65%)', borderRadius: '50%' }} />

      {/* 扫描线 */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 1 }}>
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
          background: 'linear-gradient(to right, transparent 5%, rgba(34,211,238,0.45) 40%, rgba(34,211,238,0.7) 50%, rgba(34,211,238,0.45) 60%, transparent 95%)',
          animation: 'scanLine 9s linear infinite',
        }} />
      </div>

      <style>{`
        @keyframes scanLine {
          0% { top: 0%; opacity: 0; }
          3% { opacity: 1; }
          97% { opacity: 0.7; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      {/* 顶部导航栏 */}
      <div style={{ flexShrink: 0, position: 'relative', zIndex: 10 }}>
        <Header />
      </div>

      {/* AI 评价进度遮罩 */}
      {pageState === 'evaluating' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center"
          style={{ zIndex: 50, background: 'rgba(1,11,26,0.82)', backdropFilter: 'blur(6px)' }}>
          <div className="flex flex-col items-center gap-6" style={{ width: '360px' }}>
            {/* 旋转圆环 */}
            <div className="relative w-24 h-24">
              <svg viewBox="0 0 96 96" className="w-full h-full" style={{ animation: 'spin 1.4s linear infinite' }}>
                <circle cx="48" cy="48" r="40" fill="none" stroke="rgba(34,211,238,0.12)" strokeWidth="6" />
                <circle cx="48" cy="48" r="40" fill="none" stroke="#22d3ee" strokeWidth="6"
                  strokeLinecap="round" strokeDasharray={`${evalProgress * 2.51} 251`}
                  style={{ filter: 'drop-shadow(0 0 8px rgba(34,211,238,0.8))', transformOrigin: 'center', transform: 'rotate(-90deg)' }} />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span style={{ fontSize: '18px', fontWeight: 900, color: '#22d3ee', fontFamily: 'monospace' }}>
                  {evalProgress}%
                </span>
              </div>
            </div>
            <div className="text-center">
              <p className="font-bold mb-1" style={{ fontSize: '18px', color: '#fff', letterSpacing: '0.06em' }}>AI 评价分析中</p>
              <p style={{ fontSize: '17px', color: 'white' }}>正在对课程进行多维度智能评估，请稍候...</p>
            </div>
            {/* 进度条 */}
            <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(34,211,238,0.1)' }}>
              <div className="h-full rounded-full transition-all duration-200"
                style={{ width: `${evalProgress}%`, background: 'linear-gradient(to right, #22d3ee, #14b8a6)', boxShadow: '0 0 8px rgba(34,211,238,0.6)' }} />
            </div>
            {/* 步骤提示 */}
            <div className="flex flex-col gap-1.5 w-full">
              {['解析视频内容与教学结构...', '提取多维度评价特征...', '生成 AI 综合评分报告...'].map((step, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: evalProgress > (i + 1) * 30 ? 'rgba(34,211,238,0.2)' : 'rgba(255,255,255,0.05)', border: `1px solid ${evalProgress > (i + 1) * 30 ? 'rgba(34,211,238,0.5)' : 'rgba(255,255,255,0.1)'}` }}>
                    {evalProgress > (i + 1) * 30
                      ? <i className="ri-check-line" style={{ fontSize: '16px', color: '#22d3ee' }} />
                      : <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.2)' }} />}
                  </div>
                  <span style={{ fontSize: '16px', color: evalProgress > (i + 1) * 30 ? 'rgba(34,211,238,0.8)' : 'rgba(255,255,255,0.3)' }}>{step}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 主内容区：三列布局 — 针对 3:4 大屏优化 */}
      <div
        className="px-2 pb-2 pt-1.5"
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: '21% 49% 30%',
          gap: '8px',
          position: 'relative',
          zIndex: 10,
        }}
      >
        {/* ── 左列：基本信息 + 课程资料/上传 ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minHeight: 0, overflow: 'hidden' }}>
          <BasicInfoPanel />
          {pageState === 'done' ? <CourseMaterialsPanel uploadedFile={uploadedVideoFile} onEvaluateComplete={handleReset} /> : <EmptyMaterialsPanel onVideoUpload={setUploadedVideoFile} onUploadingStateChange={setIsUploading} />}
        </div>

        {/* ── 中列 ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minHeight: 0 }}>
          {pageState === 'done' ? (
            <>
              <PanelCard accentColor="#22d3ee" style={{ flex: '0 0 63%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <AIRadarPanel />
              </PanelCard>
              <PanelCard accentColor="#14b8a6" style={{ flex: '1 1 0', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <DimensionLineChart />
              </PanelCard>
            </>
          ) : (
            <>
              <PanelCard accentColor="#22d3ee" style={{ flex: '0 0 63%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <EmptyEvalCenter />
              </PanelCard>
              <PanelCard accentColor="#14b8a6" style={{ flex: '1 1 0', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

                <div className="flex-1 flex flex-col items-center justify-center gap-4">
                   <button 
                     onClick={!(isUploading || !uploadedVideoFile) ? handleStartEval : undefined} 
                     disabled={isUploading || !uploadedVideoFile} 
                     className={`flex items-center gap-2.5 px-8 py-3 rounded-xl whitespace-nowrap transition-all duration-300 ${isUploading || !uploadedVideoFile ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:scale-105'}`} 
                     style={{ 
                       background: isUploading || !uploadedVideoFile ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, rgba(34,211,238,0.22), rgba(20,184,166,0.18))', 
                       border: isUploading || !uploadedVideoFile ? '1px dashed rgba(255,255,255,0.2)' : '1px solid rgba(34,211,238,0.55)', 
                       color: isUploading || !uploadedVideoFile ? 'rgba(255,255,255,0.4)' : '#22d3ee', 
                       fontSize: '17px', fontWeight: 700, 
                       boxShadow: isUploading || !uploadedVideoFile ? 'none' : '0 0 24px rgba(34,211,238,0.25)', 
                       letterSpacing: '0.06em' 
                     }}> 
                      {isUploading ? (<> <i className="ri-loader-4-line animate-spin" style={{ fontSize: '18px' }} /> 视频解析中... </>) : (<> <i className="ri-sparkling-line" style={{ fontSize: '18px' }} /> 开始 AI 评价 </>)} 
                   </button>

                   {!uploadedVideoFile && !isUploading && (
                     <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <i className="ri-information-line text-slate-400"></i>
                        <span className="text-slate-300 text-sm">请先完成左侧资料区视频上传</span>
                     </div>
                   )}
                </div>
              </PanelCard>
            </>
          )}
        </div>

        {/* ── 右列 ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minHeight: 0, overflow: 'hidden' }}>
          {pageState === 'done' ? (
            <>
              <PanelCard accentColor="#a78bfa" style={{ flex: '0 0 38%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <AICommentPanel />
              </PanelCard>
              <PanelCard accentColor="#10b981" style={{ flex: '1 1 0', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <ImprovementPanel />
              </PanelCard>
            </>
          ) : (
            <EmptyRightPanel />
          )}
        </div>
      </div>

    </div>
  );
}

export default function TeacherDetailPage() {
  return (
    <EvalProvider>
      <TeacherDetailPageInner />
    </EvalProvider>
  );
}




